import React from 'react';
import { HelpCircle, MessageCircle, FileText, Video, Mail, ExternalLink } from 'lucide-react';
import Button from '../../components/Button';

const Support: React.FC = () => {
  const faqs = [
    {
      question: 'How do I create my first mockup?',
      answer: 'Simply upload your logo or design, choose a template, customize the colors and settings, then download your professional mockup.'
    },
    {
      question: 'What file formats are supported?',
      answer: 'We support PNG, JPG, SVG, and WEBP formats for uploads. You can download your mockups in PNG or JPG format.'
    },
    {
      question: 'Can I use the mockups commercially?',
      answer: 'Yes! All mockups created with BrandScapes AI can be used for commercial purposes without attribution.'
    },
    {
      question: 'How do I cancel my subscription?',
      answer: 'You can cancel your subscription anytime from the User Settings page. Your access will continue until the end of your billing period.'
    }
  ];

  const resources = [
    {
      title: 'Getting Started Guide',
      description: 'Learn the basics of creating mockups',
      icon: <FileText className="w-6 h-6" />,
      link: '#'
    },
    {
      title: 'Video Tutorials',
      description: 'Watch step-by-step tutorials',
      icon: <Video className="w-6 h-6" />,
      link: '#'
    },
    {
      title: 'API Documentation',
      description: 'Integrate mockups into your workflow',
      icon: <FileText className="w-6 h-6" />,
      link: '#'
    }
  ];

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8">
        <h1 className="text-h2 font-heading text-text-dark mb-2">
          Help & Support
        </h1>
        <p className="text-body text-body-grey">
          Get help with BrandScapes AI
        </p>
      </div>

      {/* Contact Support */}
      <div className="bg-gradient-to-r from-brand-purple to-brand-blue rounded-xl p-8 mb-8 text-white">
        <div className="flex flex-col md:flex-row items-center justify-between">
          <div className="mb-6 md:mb-0">
            <h2 className="text-h3 font-heading mb-2">
              Need personalized help?
            </h2>
            <p className="text-lg opacity-90">
              Our support team is here to assist you
            </p>
          </div>
          <div className="flex gap-4">
            <Button variant="secondary" size="medium">
              <MessageCircle className="w-5 h-5 mr-2" />
              Start Live Chat
            </Button>
            <Button variant="secondary" size="medium">
              <Mail className="w-5 h-5 mr-2" />
              Email Support
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* FAQs */}
        <div className="lg:col-span-2">
          <h2 className="text-xl font-semibold text-text-dark mb-6">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-medium text-text-dark mb-2 flex items-start">
                  <HelpCircle className="w-5 h-5 mr-2 text-brand-purple flex-shrink-0 mt-0.5" />
                  {faq.question}
                </h3>
                <p className="text-body-grey ml-7">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Resources */}
        <div>
          <h2 className="text-xl font-semibold text-text-dark mb-6">
            Resources
          </h2>
          <div className="space-y-4">
            {resources.map((resource, index) => (
              <a
                key={index}
                href={resource.link}
                className="block bg-white rounded-lg shadow-sm p-6 hover:shadow-md transition-all"
              >
                <div className="flex items-start">
                  <div className="text-brand-purple mr-4">
                    {resource.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="font-medium text-text-dark mb-1 flex items-center">
                      {resource.title}
                      <ExternalLink className="w-4 h-4 ml-2 text-gray-400" />
                    </h3>
                    <p className="text-sm text-body-grey">{resource.description}</p>
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
